import { supabase } from '@/utils/supabase';

export interface MarketingReel {
  id: string;
  channel_id: string;
  post_id: string;
  handle: string;
  owner_id?: string;
  owner_handle?: string | null;
  collaborator_id?: string | null;
  post_url: string;
  caption: string;
  posted_at: string;
  captured_at: string;
  views: number | null;
  plays: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  lo_user_id: string | null;
  monitoring_kind: string;
  reels_error: string | null;
}
export interface MarketingReelData {
  enabled: boolean;
  posts: MarketingReel[];
  unavailable: boolean;
}

export function isMarketingReel(value: unknown): value is MarketingReel {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    (row.owner_handle === undefined ||
      row.owner_handle === null ||
      (typeof row.owner_handle === 'string' && /^[a-z0-9._]{1,30}$/.test(row.owner_handle))) &&
    (row.collaborator_id === undefined ||
      row.collaborator_id === null ||
      (typeof row.collaborator_id === 'string' &&
        /^[0-9]+$/.test(row.collaborator_id) &&
        typeof row.owner_id === 'string' &&
        /^[0-9]+$/.test(row.owner_id) &&
        row.collaborator_id !== row.owner_id &&
        typeof row.owner_handle === 'string' &&
        row.owner_handle !== row.handle)) &&
    ['id', 'channel_id', 'post_id', 'handle', 'caption', 'monitoring_kind'].every(
      (key) => typeof row[key] === 'string'
    ) &&
    typeof row.post_url === 'string' &&
    /^https:\/\/www\.instagram\.com\/reel\/[A-Za-z0-9_-]{5,30}\/$/.test(row.post_url) &&
    ['posted_at', 'captured_at'].every(
      (key) => typeof row[key] === 'string' && Number.isFinite(Date.parse(row[key] as string))
    ) &&
    ['views', 'plays', 'likes', 'comments', 'shares'].every(
      (key) => row[key] === null || (Number.isSafeInteger(row[key]) && Number(row[key]) >= 0)
    ) &&
    (row.lo_user_id === null || typeof row.lo_user_id === 'string') &&
    (row.reels_error === null || typeof row.reels_error === 'string')
  );
}

/** One row per Instagram post; keep each visible account's relationship to it.
 * Metrics come from one latest capture, never a sum across collaborators.
 * Call after access/account scoping so a group cannot widen the visible roster.
 */
export function groupMarketingReels(posts: MarketingReel[]) {
  const groups = new Map<string, { post: MarketingReel; accounts: MarketingReel[] }>();
  for (const post of posts) {
    const group = groups.get(post.post_id);
    if (!group) {
      groups.set(post.post_id, { post, accounts: [post] });
      continue;
    }
    if (Date.parse(post.captured_at) > Date.parse(group.post.captured_at)) {
      group.post = post;
    }
    const index = group.accounts.findIndex((account) => account.channel_id === post.channel_id);
    if (index < 0) {
      group.accounts.push(post);
    } else if (Date.parse(post.captured_at) > Date.parse(group.accounts[index].captured_at)) {
      group.accounts[index] = post;
    }
  }
  return [...groups.values()];
}

/** Called once by the existing Studio refresh owner; failed optional data never breaks Studio. */
export async function loadMarketingReels(): Promise<MarketingReelData> {
  try {
    const { data, error } = await supabase.rpc('marketing_reel_results');
    if (
      error ||
      !data ||
      typeof data.enabled !== 'boolean' ||
      !Array.isArray(data.posts) ||
      !data.posts.every(isMarketingReel)
    ) {
      throw new Error('Reel measurements unavailable');
    }
    return { ...data, unavailable: false };
  } catch {
    return { posts: [], enabled: false, unavailable: true };
  }
}
